/**
 * SSR loader safety net.
 *
 * A route loader that rejects during server rendering (a transient database
 * read failure, a network blip between the worker and the data API) used to
 * bubble up as a bare 500 "HTTPError" with no stack in the server logs — the
 * visitor got the generic error page and we got no clue why.
 *
 * `safeLoad` does two things:
 *  - logs the real error (message + stack + cause) so it shows up in logs,
 *  - never rethrows, so the page still renders and the browser refetches the
 *    data client-side instead of the visitor hitting an error page.
 */
export async function safeLoad<T>(
  label: string,
  work: Promise<T> | (() => Promise<T>),
): Promise<T | undefined> {
  try {
    return await (typeof work === "function" ? work() : work);
  } catch (error) {
    logSsrError(label, error);
    return undefined;
  }
}

/** Awaits several loader reads, logging failures individually. */
export function safeLoadAll(
  entries: Array<[label: string, work: Promise<unknown>]>,
): Promise<unknown> {
  return Promise.all(entries.map(([label, work]) => safeLoad(label, work)));
}

export function logSsrError(label: string, error: unknown) {
  const detail =
    error instanceof Error
      ? error
      : new Error(`[SSR ${label}] non-error thrown: ${safeStringify(error)}`);
  console.error(`[SSR ${label}] loader failed:`, detail);
  const cause = (error as { cause?: unknown })?.cause;
  if (cause) console.error(`[SSR ${label}] cause:`, cause);
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
